import React, { useEffect, useState } from "react";
import {
  StreamVideo,
  StreamTheme,
  StreamCall,
  StreamVideoClient,
  SpeakerLayout,
  CallControls,
} from "@stream-io/video-react-sdk";
import { useLocation } from "react-router-dom";
import { useTokenRefresh } from "../../hooks/useTokenRefresh";
import ReviewModal from "../components/ReviewModal";

import "@stream-io/video-react-sdk/dist/css/styles.css";

export default function ClientVideo() {
  // Use token refresh hook
  useTokenRefresh('client');
  
  const location = useLocation();
  const [client, setClient] = useState(null);
  const [call, setCall] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [lawyerName, setLawyerName] = useState('');
  const [appointmentId, setAppointmentId] = useState('');
  const [lawyerId, setLawyerId] = useState('');
  const [clientId, setClientId] = useState('');

  // Extracting URL parameters for token, userId, callId, and name
  const params = new URLSearchParams(location.search);
  const token = JSON.parse(params.get("token")); // Extract token
  const userId = JSON.parse(params.get("userId")); // Extract userId
  const callId = JSON.parse(params.get("callId")); // Extract callId
  const name = JSON.parse(params.get("name")); // Extract name
  
  // Extract additional parameters for review
  const lawyerNameParam = params.get("lawyerName");
  const appointmentIdParam = params.get("appointmentId");
  const lawyerIdParam = params.get("lawyerId");
  
  // Set state for review modal
  useEffect(() => {
    if (lawyerNameParam) setLawyerName(decodeURIComponent(lawyerNameParam));
    if (appointmentIdParam) setAppointmentId(appointmentIdParam);
    if (lawyerIdParam) setLawyerId(lawyerIdParam);
    setClientId(userId);
  }, [lawyerNameParam, appointmentIdParam, lawyerIdParam, userId]);

  // User object (as per the demo)
  const user = {
    id: userId,
    name: name,
    image: "https://getstream.io/random_svg/?id=oliver&name=Oliver", // Default image URL
  };

  const apiKey = "r2np5pp6p7w7"; // Your Stream API key

  // 1. init StreamVideoClient
  useEffect(() => {
    if (!token) return console.error("Missing Stream token");
    const vClient = new StreamVideoClient({ apiKey, user, token });
    setClient(vClient);
    return () => {
      vClient.disconnectUser();
      setClient(null);
    };
  }, [token]);

  // 2. join the call
  useEffect(() => {
    if (!client || !callId) return;
    const vCall = client.call("default", callId);
    vCall.join({ create: true }).catch(console.error);
    setCall(vCall);
    return () => {
      vCall.leave().catch(console.error);
      setCall(null);
    };
  }, [client, callId]);

  // 3. best-effort leave on tab close/reload
  useEffect(() => {
    if (!call) return;
    const handler = (e) => {
      call.leave().catch(() => {});
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [call]);

  if (!client || !call) return <div>Loading…</div>;

  // Called after Stream has left the call (either Leave or End-for-everyone)
  const leaveAndClose = () => {
    // Show review modal instead of closing immediately
    if (appointmentId && lawyerId && clientId) {
      setShowReviewModal(true);
    } else {
      // close the tab once the SDK has fully left
      window.close();
    }
  };

  const handleReviewSubmit = (result) => {
    console.log('Review submitted:', result);
    // Close the tab after review submission
    window.close();
  };

  const handleReviewClose = () => {
    // Close the tab when user skips review
    window.close();
  };

  return (
    <>
      <StreamVideo client={client}>
        <StreamTheme className="my-theme-overrides">
          <StreamCall call={call}>
            <SpeakerLayout />
            {/* keep the built-in controls, but add onLeave to close the tab */}
            <CallControls onLeave={leaveAndClose} />
          </StreamCall>
        </StreamTheme>
      </StreamVideo>
      
      <ReviewModal
        isOpen={showReviewModal}
        onClose={handleReviewClose}
        onSubmit={handleReviewSubmit}
        lawyerName={lawyerName}
        appointmentId={appointmentId}
        lawyerId={lawyerId}
        clientId={clientId}
      />
    </>
  );
}
